/**
 * IMPORT CSV - ÉTAPE 4 : RESULT
 * Rapport d'import avec progress bar
 */

import { CheckCircle, Download, AlertCircle } from 'lucide-react';
import { Button, Badge } from '../ui';
import type { CSVImportResult } from '../../types/csv';
import { exportErrorsToCSV } from '../../lib/csv-import';

interface ImportStepResultProps<T> {
  result: CSVImportResult<T> | null;
  isImporting: boolean;
  progress: { current: number; total: number } | null;
  entityNamePlural: string;
}

export function ImportStepResult<T>({
  result,
  isImporting,
  progress,
  entityNamePlural,
}: ImportStepResultProps<T>) {
  const handleExportErrors = () => {
    if (result && result.errors.length > 0) {
      const filename = `erreurs-import-${entityNamePlural}-${new Date().toISOString().split('T')[0]}.csv`;
      exportErrorsToCSV(result.errors, filename);
    }
  };

  // Progress bar
  if (isImporting && progress) {
    const percentage = Math.round((progress.current / progress.total) * 100);

    return (
      <div className="space-y-8">
        <div className="text-center">
          <h3 className="text-2xl font-semibold mb-3">Import en cours...</h3>
          <p className="text-base text-gray-600">
            {progress.current} / {progress.total} lignes traitées
          </p>
        </div>

        <div className="space-y-3">
          <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
            <div
              className="bg-blue-600 h-full transition-all duration-300 ease-out"
              style={{ width: `${percentage}%` }}
            />
          </div>
          <div className="text-center text-xl font-medium text-gray-700">{percentage}%</div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center justify-center space-x-3 text-base text-blue-800">
            <AlertCircle className="h-6 w-6" />
            <span>Veuillez patienter, l'import est en cours...</span>
          </div>
        </div>
      </div>
    );
  }

  // Résultat final
  if (result) {
    const hasErrors = result.errors.length > 0;
    const totalProcessed = result.created + result.updated + result.errors.length;
    const successRate = Math.round(((result.created + result.updated) / totalProcessed) * 100);
    const durationSeconds = (result.duration / 1000).toFixed(1);

    return (
      <div className="space-y-8">
        <div className="text-center">
          <div
            className={`
            inline-flex items-center justify-center w-24 h-24 rounded-full mb-6
            ${hasErrors ? 'bg-yellow-100' : 'bg-green-100'}
          `}
          >
            {hasErrors ? (
              <AlertCircle className="h-14 w-14 text-yellow-600" />
            ) : (
              <CheckCircle className="h-14 w-14 text-green-600" />
            )}
          </div>

          <h3 className="text-2xl font-semibold mb-3">
            {hasErrors ? 'Import terminé avec erreurs' : 'Import réussi !'}
          </h3>
          <p className="text-base text-gray-600">
            {totalProcessed} lignes traitées en {durationSeconds}s
          </p>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-3 gap-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
            <div className="text-4xl font-bold text-green-700">{result.created}</div>
            <div className="text-sm text-green-600 mt-2">
              {result.created > 1 ? 'Créés' : 'Créé'}
            </div>
          </div>

          {result.updated > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
              <div className="text-4xl font-bold text-blue-700">{result.updated}</div>
              <div className="text-sm text-blue-600 mt-2">
                {result.updated > 1 ? 'Mis à jour' : 'Mis à jour'}
              </div>
            </div>
          )}

          <div
            className={`
            border rounded-lg p-6 text-center
            ${hasErrors ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}
          `}
          >
            <div className={`text-4xl font-bold ${hasErrors ? 'text-red-700' : 'text-gray-500'}`}>
              {result.errors.length}
            </div>
            <div className={`text-sm mt-2 ${hasErrors ? 'text-red-600' : 'text-gray-500'}`}>
              {result.errors.length > 1 ? 'Erreurs' : 'Erreur'}
            </div>
          </div>
        </div>

        {/* Taux de succès */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <span className="text-base font-medium text-gray-700">Taux de succès</span>
            <Badge
              variant={
                successRate === 100 ? 'default' : successRate >= 80 ? 'secondary' : 'destructive'
              }
            >
              {successRate}%
            </Badge>
          </div>
        </div>

        {/* Liste des erreurs */}
        {hasErrors && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-lg font-medium text-gray-900">
                Détail des erreurs ({result.errors.length})
              </h4>
              <Button variant="outline" size="default" onClick={handleExportErrors}>
                <Download className="mr-2 h-4 w-4" />
                Exporter les erreurs
              </Button>
            </div>

            <div className="border rounded-lg max-h-[300px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Ligne</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Champ</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Erreur</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {result.errors.map((error, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{error.line}</td>
                      <td className="px-4 py-3 text-gray-900 whitespace-nowrap">{error.field}</td>
                      <td className="px-4 py-3 text-red-600">{error.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Message de succès */}
        {!hasErrors && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <div className="flex items-center space-x-4">
              <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
              <div className="text-base text-green-800">
                <p className="font-medium text-lg">
                  Tous les {entityNamePlural} ont été importés avec succès !
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
}
